<script setup>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import PaginationDetail from 'dashboard/components/table/PaginationDetail.vue';

const props = defineProps({
  agents: { type: Array, required: true },
  meta: {
    type: Object,
    required: true, // { count, currentPage, perPage }
  },
  showEditAction: { type: Function, required: true },
  showDeleteAction: { type: Function, required: true },
  getAgentRoleName: { type: Function, required: true },
  getAgentRolePermissions: { type: Function, required: true },
  loading: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['edit', 'delete', 'pageChange']);

const table = {
  getState() {
    return {
      pagination: {
        pageIndex: props.meta.currentPage - 1,
        pageSize: props.meta.perPage,
      },
    };
  },
  setPageIndex(idx) {
    const page = idx + 1;
    if (page === props.meta.currentPage) return;
    emit('pageChange', page);
  },
  previousPage() {
    if (props.meta.currentPage > 1) {
      this.setPageIndex(props.meta.currentPage - 2);
    }
  },
  nextPage() {
    if (props.meta.currentPage < this.getPageCount()) {
      this.setPageIndex(props.meta.currentPage);
    }
  },
  getPageCount() {
    return Math.max(1, Math.ceil(props.meta.count / props.meta.perPage));
  },
  getRowCount() {
    return props.meta.count;
  },
};
</script>

<template>
  <div class="setting-table-container">
    <table>
      <tbody class="divide-y divide-slate-50 dark:divide-n-vars-black-bd/80 text-slate-700 dark:text-slate-300">
        <tr v-for="agent in agents" :key="agent.email">
          <td class="ltr:pr-4 rtl:pl-4">
            <div class="flex flex-row items-center gap-3">
              <div class="flex gap-3 w-[12rem] flex-none">
                <Thumbnail :src="agent.thumbnail" :username="agent.name" size="32px" />
                <div class="min-w-0">
                  <span class="block font-semibold text-sm text-n-gray-7 dark:text-n-gray-3 capitalize truncate">{{ agent.name }}</span>
                  <span class="truncate text-xs text-n-gray-5">{{ agent.email }}</span>
                </div>
              </div>
              <span
                :class="[
                  'text-xs px-[6px] py-[5px] rounded-lg shrink-0',
                  agent.availability_status === 'online'
                    ? 'text-grass-500 bg-[#12B76A]/5'
                    : 'text-n-gray-5 bg-[#F5F5F5]/5'
                ]"
              >
                {{ agent.availability_status }}
              </span>
            </div>
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div v-if="agent.confirmed" class="flex gap-2 items-center">
              <span class="text-grass-500">{{ $t('AGENT_MGMT.LIST.VERIFIED') }}</span>
              <LucideIcon name="check-check" size="14" class="text-grass-500" />
            </div>
            <span v-else class="text-n-gray-6">{{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}</span>
          </td>
          <td class="relative py-4 ltr:pr-4 rtl:pl-4">
            <span
              class="block font-medium w-fit text-gray-300"
              :class="{ 'hover:text-gray-900 group cursor-pointer': agent.custom_role_id }"
            >
              {{ getAgentRoleName(agent) }}

              <div
                class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-slate-50 shadow-lg top-14 md:top-12 dark:bg-slate-800 dark:border-slate-700"
                :class="{ 'group-hover:block': agent.custom_role_id }"
              >
                <div class="flex flex-col gap-1 p-4">
                  <span class="font-semibold">{{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}</span>
                  <ul class="pl-4 mb-0 list-disc">
                    <li
                      v-for="permission in getAgentRolePermissions(agent)"
                      :key="permission"
                      class="font-normal"
                    >
                      {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
                    </li>
                  </ul>
                </div>
              </div>
            </span>
          </td>
          <td class="py-4">
            <div class="flex justify-end gap-1">
              <woot-button
                v-if="showEditAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                variant="smooth"
                size="small"
                icon="edit"
                class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg"
                @click="$emit('edit', agent)"
              />
              <woot-button
                v-if="showDeleteAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                variant="smooth"
                size="small"
                icon="dismiss-circle"
                class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg"
                :is-loading="loading[agent.id]"
                @click="$emit('delete', agent)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
    <PaginationDetail :table="table" />
</template>
